import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RxCross1 } from "react-icons/rx";
import jsPDF from "jspdf";
import { formatUSPhone } from "../utils/phoneFormatter";
import * as XLSX from "xlsx";
import useConfirm from "../hook/useConfirm";

export interface PdfDirectoryItem {
    id?: string | number;
    name: string;
    subtitle?: string;
    phone?: string;
    email?: string;
    image?: string;
}

interface PdfDirectoryExportProps {
    title: string;
    totalLength: number;
    fetchData: () => Promise<any[]>;
    fetchExcelData: () => Promise<any[]>;
    exportType: "family" | "familyMembers";
    mapItem: (item: any) => PdfDirectoryItem;
    buttonText?: string;
    defaultPerPage?: number;
    maxPerPage?: number;
}

const PdfDirectoryExport = ({
    title,
    totalLength,
    fetchData,
    fetchExcelData,
    exportType,
    mapItem,
    buttonText = "Export",
    defaultPerPage = 6,
    maxPerPage = 16,
}: PdfDirectoryExportProps) => {
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();

    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [showExportPopup, setShowExportPopup] = useState(false);

    const [itemsPerPage, setItemsPerPage] = useState(defaultPerPage);

    const [isExporting, setIsExporting] = useState(false);

    const [pdfUrl, setPdfUrl] = useState<string | null>(null);

    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleDropDown = (e: MouseEvent) => {
            if (!dropdownRef.current?.contains(e.target as Node)) {
                setIsMenuOpen(false);
            }
        }

        document.addEventListener("click", handleDropDown);

        return () => {
            document.removeEventListener("click", handleDropDown);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [pdfUrl]);

    useEffect(() => {
        if (!showExportPopup && !pdfUrl) {
            return;
        }

        const originalOverflow = document.body.style.overflow;

        const originalPaddingRight = document.body.style.paddingRight;

        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        document.body.style.overflow = "hidden";

        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }

        return () => {
            document.body.style.overflow = originalOverflow;

            document.body.style.paddingRight = originalPaddingRight;
        };
    }, [showExportPopup, pdfUrl]);

    const getImageAsBase64 = async (imageUrl?: string) => {
        if (!imageUrl) {
            return "";
        }

        try {
            const response = await fetch(imageUrl);

            if (!response.ok) {
                return "";
            }

            const blob = await response.blob();

            return await new Promise<string>((resolve) => {
                const reader = new FileReader();

                reader.onloadend = () => {
                    resolve(reader.result as string);
                };

                reader.onerror = () => {
                    resolve("");
                };

                reader.readAsDataURL(blob);
            }
            );
        } catch (error) {
            console.log(
                "Unable to load PDF image:",
                imageUrl,
                error
            );

            return "";
        }
    };

    const handleExportPdf = async () => {
        if (itemsPerPage < 1 || itemsPerPage > maxPerPage) {
            return;
        }

        try {
            setIsExporting(true);

            const rawData = await fetchData();

            const items = rawData.map(mapItem);

            if (!items.length) {
                alert(`No ${title.toLowerCase()} found to export.`);

                return;
            }

            const itemsWithImages =
                await Promise.all(
                    items.map(async (item) => {
                        const pdfImage = await getImageAsBase64(item.image);

                        return {
                            ...item,
                            pdfImage
                        };
                    })
                );

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4"
            });

            const pageWidth = pdf.internal.pageSize.getWidth();

            const pageHeight = pdf.internal.pageSize.getHeight();

            const marginX = 10;
            const marginTop = 16;
            const marginBottom = 10;

            const gapX = 5;
            const gapY = 5;

            let columns = 2;

            if (itemsPerPage === 1) {
                columns = 1;
            } else if (itemsPerPage === 2 || itemsPerPage === 3) {
                columns = 1;
            } else {
                columns = 2;
            }

            const rows = Math.ceil(itemsPerPage / columns);

            const availableWidth =
                pageWidth -
                marginX * 2 -
                gapX * (columns - 1);

            const availableHeight =
                pageHeight -
                marginTop -
                marginBottom -
                gapY * (rows - 1);

            const cardWidth = availableWidth / columns;

            const cardHeight = availableHeight / rows;

            const drawCard = (
                item: PdfDirectoryItem & { pdfImage?: string; },
                x: number,
                y: number,
                width: number,
                height: number
            ) => {

                pdf.setDrawColor(
                    220,
                    223,
                    228
                );

                pdf.setFillColor(
                    255,
                    255,
                    255
                );

                pdf.setLineWidth(0.35);

                pdf.roundedRect(
                    x,
                    y,
                    width,
                    height,
                    2.5,
                    2.5,
                    "FD"
                );

                const padding = Math.min(
                    7,
                    Math.max(
                        4,
                        width * 0.04
                    )
                );

                if (itemsPerPage <= 6) {
                    let imageRatio = 0.65;
                    let nameFontSize = 11;
                    let detailFontSize = 8.5;
                    let imageTextGap = 5;

                    if (itemsPerPage === 1) {
                        imageRatio = 0.50;
                        nameFontSize = 18;
                        detailFontSize = 12;
                        imageTextGap = 8;
                    } else if (itemsPerPage === 2) {
                        imageRatio = 0.40;
                        nameFontSize = 16;
                        detailFontSize = 11;
                        imageTextGap = 7;
                    } else if (itemsPerPage === 3) {
                        imageRatio = 0.65;
                        nameFontSize = 11.5;
                        detailFontSize = 8.8;
                        imageTextGap = 5;
                    } else if (itemsPerPage === 4) {
                        imageRatio = 0.62;
                        nameFontSize = 11.5;
                        detailFontSize = 8.8;
                        imageTextGap = 5;
                    } else if (itemsPerPage === 5) {
                        imageRatio = 0.58;
                        nameFontSize = 10.5;
                        detailFontSize = 8.3;
                        imageTextGap = 4.5;
                    } else if (itemsPerPage === 6) {
                        imageRatio = 0.55;
                        nameFontSize = 10.2;
                        detailFontSize = 8;
                        imageTextGap = 4;
                    }

                    const textX = x + padding;

                    const textWidth = width - padding * 2;

                    pdf.setTextColor(
                        32,
                        33,
                        36
                    );

                    pdf.setFont("helvetica", "bold");

                    pdf.setFontSize(nameFontSize);

                    const nameLines =
                        pdf.splitTextToSize(
                            item.name ||
                            "No Name",
                            textWidth
                        );

                    const subtitleLines =
                        item.subtitle
                            ? pdf.splitTextToSize(
                                item.subtitle,
                                textWidth
                            )
                            : [];

                    const phone = formatUSPhone(item.phone || "");

                    const phoneLines =
                        pdf.splitTextToSize(
                            `Phone: ${phone || "N/A"}`,
                            textWidth
                        );

                    const emailLines =
                        pdf.splitTextToSize(
                            `Email: ${item.email || "N/A"}`,
                            textWidth
                        );


                    const nameLineHeight =
                        itemsPerPage === 1
                            ? 7.5
                            : itemsPerPage === 2
                                ? 6
                                : itemsPerPage === 3
                                    ? 5
                                    : itemsPerPage === 4
                                        ? 4.8
                                        : 4.4;

                    const detailLineHeight =
                        itemsPerPage === 1
                            ? 5.8
                            : itemsPerPage === 2
                                ? 4.8
                                : itemsPerPage === 3
                                    ? 4
                                    : itemsPerPage === 4
                                        ? 3.9
                                        : 3.7;

                    const textSpacing =
                        itemsPerPage === 1
                            ? 4
                            : itemsPerPage === 2
                                ? 3
                                : 2.5;


                    const nameHeight =
                        Math.min(
                            nameLines.length,
                            2
                        ) *
                        nameLineHeight;

                    const subtitleHeight =
                        subtitleLines.length
                            ? Math.min(
                                subtitleLines.length,
                                2
                            ) *
                            detailLineHeight +
                            textSpacing
                            : 0;

                    const phoneHeight =
                        Math.min(
                            phoneLines.length,
                            2
                        ) *
                        detailLineHeight;

                    const emailHeight =
                        Math.min(
                            emailLines.length,
                            3
                        ) *
                        detailLineHeight;

                    const textBlockHeight =
                        nameHeight +
                        subtitleHeight +
                        phoneHeight +
                        emailHeight +
                        textSpacing * 2;


                    const maxImageByWidth =
                        width *
                        imageRatio;

                    const maxImageByHeight =
                        Math.max(
                            20,
                            height -
                            padding * 2 -
                            textBlockHeight -
                            imageTextGap
                        );

                    let imageSize =
                        Math.min(
                            maxImageByWidth,
                            maxImageByHeight
                        );


                    imageSize = Math.max(
                        20,
                        imageSize
                    );


                    const groupHeight =
                        imageSize +
                        imageTextGap +
                        textBlockHeight;


                    const groupTop =
                        y +
                        Math.max(
                            padding,
                            (height -
                                groupHeight) /
                            2
                        );

                    const imageX =
                        x +
                        (width -
                            imageSize) /
                        2;

                    const imageY =
                        groupTop;


                    if (item.pdfImage) {
                        try {
                            pdf.addImage(
                                item.pdfImage,
                                "JPEG",
                                imageX,
                                imageY,
                                imageSize,
                                imageSize,
                                undefined,
                                "FAST"
                            );
                        } catch {

                        }
                    } else {
                        pdf.setFillColor(
                            245,
                            246,
                            248
                        );

                        pdf.setDrawColor(
                            225,
                            227,
                            231
                        );

                        pdf.roundedRect(
                            imageX,
                            imageY,
                            imageSize,
                            imageSize,
                            2,
                            2,
                            "FD"
                        );

                        pdf.setFont(
                            "helvetica",
                            "normal"
                        );

                        pdf.setFontSize(
                            itemsPerPage === 1
                                ? 9
                                : 7
                        );

                        pdf.setTextColor(
                            150,
                            150,
                            150
                        );

                        pdf.text(
                            "No Image",
                            imageX +
                            imageSize /
                            2,
                            imageY +
                            imageSize /
                            2,
                            {
                                align: "center",
                            }
                        );
                    }


                    let textY =
                        imageY +
                        imageSize +
                        imageTextGap +
                        nameLineHeight;


                    pdf.setFont(
                        "helvetica",
                        "bold"
                    );

                    pdf.setFontSize(
                        nameFontSize
                    );

                    pdf.text(
                        nameLines.slice(
                            0,
                            2
                        ),
                        textX +
                        textWidth / 2,
                        textY,
                        {
                            align: "center",
                        }
                    );

                    textY +=
                        nameHeight +
                        textSpacing;


                    if (
                        subtitleLines.length
                    ) {
                        pdf.setFont(
                            "helvetica",
                            "normal"
                        );

                        pdf.setFontSize(
                            detailFontSize
                        );

                        pdf.text(
                            subtitleLines.slice(
                                0,
                                2
                            ),
                            textX +
                            textWidth /
                            2,
                            textY,
                            {
                                align: "center",
                            }
                        );

                        textY +=
                            Math.min(
                                subtitleLines.length,
                                2
                            ) *
                            detailLineHeight +
                            textSpacing;
                    }


                    pdf.setFont(
                        "helvetica",
                        "normal"
                    );

                    pdf.setFontSize(
                        detailFontSize
                    );

                    pdf.text(
                        phoneLines.slice(
                            0,
                            2
                        ),
                        textX +
                        textWidth / 2,
                        textY,
                        {
                            align: "center",
                        }
                    );

                    textY +=
                        phoneHeight +
                        textSpacing;


                    pdf.text(
                        emailLines.slice(
                            0,
                            3
                        ),
                        textX +
                        textWidth / 2,
                        textY,
                        {
                            align: "center",
                        }
                    );

                    return;
                }


                const imageSize = Math.max(
                    15,
                    Math.min(
                        28,
                        height -
                        padding * 2,
                        width * 0.3
                    )
                );

                const imageX =
                    x + padding;

                const imageY =
                    y +
                    (height -
                        imageSize) /
                    2;

                if (item.pdfImage) {
                    try {
                        pdf.addImage(
                            item.pdfImage,
                            "JPEG",
                            imageX,
                            imageY,
                            imageSize,
                            imageSize,
                            undefined,
                            "FAST"
                        );
                    } catch {

                    }
                } else {
                    pdf.setFillColor(
                        245,
                        246,
                        248
                    );

                    pdf.setDrawColor(
                        225,
                        227,
                        231
                    );

                    pdf.roundedRect(
                        imageX,
                        imageY,
                        imageSize,
                        imageSize,
                        2,
                        2,
                        "FD"
                    );

                    pdf.setFont(
                        "helvetica",
                        "normal"
                    );

                    pdf.setFontSize(7);

                    pdf.setTextColor(
                        150,
                        150,
                        150
                    );

                    pdf.text(
                        "No Image",
                        imageX +
                        imageSize /
                        2,
                        imageY +
                        imageSize /
                        2,
                        {
                            align: "center",
                        }
                    );
                }

                const textX =
                    imageX +
                    imageSize +
                    padding;

                const textWidth =
                    x +
                    width -
                    padding -
                    textX;

                if (textWidth < 20) {
                    return;
                }

                pdf.setTextColor(
                    32,
                    33,
                    36
                );

                pdf.setFont(
                    "helvetica",
                    "bold"
                );

                pdf.setFontSize(10);

                const nameLines =
                    pdf.splitTextToSize(
                        item.name ||
                        "No Name",
                        textWidth
                    );

                const subtitleLines =
                    item.subtitle
                        ? pdf.splitTextToSize(
                            item.subtitle,
                            textWidth
                        )
                        : [];

                pdf.setFont(
                    "helvetica",
                    "normal"
                );

                pdf.setFontSize(8.5);

                const phone =
                    formatUSPhone(
                        item.phone || ""
                    );

                const phoneLines =
                    pdf.splitTextToSize(
                        `Phone: ${phone || "N/A"
                        }`,
                        textWidth
                    );

                const emailLines =
                    pdf.splitTextToSize(
                        `Email: ${item.email ||
                        "N/A"
                        }`,
                        textWidth
                    );

                const nameLineHeight = 4.2;
                const detailLineHeight = 3.8;
                const spacing = 2.5;

                const totalTextHeight =
                    Math.min(
                        nameLines.length,
                        2
                    ) *
                    nameLineHeight +
                    (subtitleLines.length
                        ? detailLineHeight +
                        spacing
                        : 0) +
                    spacing +
                    Math.min(
                        phoneLines.length,
                        2
                    ) *
                    detailLineHeight +
                    spacing +
                    Math.min(
                        emailLines.length,
                        3
                    ) *
                    detailLineHeight;

                let textY =
                    y +
                    (height -
                        totalTextHeight) /
                    2 +
                    nameLineHeight;

                pdf.setFont(
                    "helvetica",
                    "bold"
                );

                pdf.setFontSize(10);

                pdf.text(
                    nameLines.slice(
                        0,
                        2
                    ),
                    textX,
                    textY
                );

                textY +=
                    Math.min(
                        nameLines.length,
                        2
                    ) *
                    nameLineHeight +
                    spacing;

                if (
                    subtitleLines.length
                ) {
                    pdf.setFont(
                        "helvetica",
                        "normal"
                    );

                    pdf.setFontSize(8.5);

                    pdf.text(
                        subtitleLines.slice(
                            0,
                            2
                        ),
                        textX,
                        textY
                    );

                    textY +=
                        Math.min(
                            subtitleLines.length,
                            2
                        ) *
                        detailLineHeight +
                        spacing;
                }

                pdf.setFont(
                    "helvetica",
                    "normal"
                );

                pdf.setFontSize(8.5);

                pdf.text(
                    phoneLines.slice(
                        0,
                        2
                    ),
                    textX,
                    textY
                );

                textY +=
                    Math.min(
                        phoneLines.length,
                        2
                    ) *
                    detailLineHeight +
                    spacing;

                pdf.text(
                    emailLines.slice(
                        0,
                        3
                    ),
                    textX,
                    textY
                );
            };


            itemsWithImages.forEach(
                (item, index) => {
                    const indexOnPage =
                        index %
                        itemsPerPage;


                    if (
                        index > 0 &&
                        indexOnPage === 0
                    ) {
                        pdf.addPage();
                    }

                    const columnIndex =
                        indexOnPage %
                        columns;

                    const rowIndex =
                        Math.floor(
                            indexOnPage /
                            columns
                        );

                    const x =
                        marginX +
                        columnIndex *
                        (cardWidth +
                            gapX);

                    const y =
                        marginTop +
                        rowIndex *
                        (cardHeight +
                            gapY);

                    drawCard(
                        item,
                        x,
                        y,
                        cardWidth,
                        cardHeight
                    );
                }
            );


            const pdfBlob =
                pdf.output("blob");

            const newPdfUrl =
                URL.createObjectURL(
                    pdfBlob
                );

            if (pdfUrl) {
                URL.revokeObjectURL(
                    pdfUrl
                );
            }

            setPdfUrl(newPdfUrl);

            setShowExportPopup(false);
        } catch (error) {
            console.error(
                `${title} PDF export error:`,
                error
            );

            alert(`Unable to export ${title.toLowerCase()}.`);
        } finally {
            setIsExporting(false);
        }
    }

    const convertToCsv = (rows: Record<string, any>[]) => {
        if (!rows.length) return "";

        const headers = Object.keys(rows[0]);
        const escapeCsvValue = (value: any) => {
            const normalizedValue =
                value === null || value === undefined
                    ? ""
                    : typeof value === "object"
                        ? JSON.stringify(value)
                        : String(value);

            return `"${normalizedValue.replace(/"/g, '""')}"`;
        };

        return [
            headers.map(escapeCsvValue).join(","),
            ...rows.map((row) =>
                headers.map((header) => escapeCsvValue(row[header])).join(",")
            ),
        ].join("\r\n");
    };

    const downloadCsv = (rows: Record<string, any>[], fileName: string) => {
        if (!rows.length) return;
        const csvContent = "\uFEFF" + convertToCsv(rows);
        const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    const handleMemberExportAll = async (format: "csv" | "excel") => {
        try {
            setIsExporting(true);

            const records = await fetchExcelData();

            if (!Array.isArray(records) || records.length === 0) {
                setConfirmData({
                    title: "Warning",
                    summary:
                        exportType === "familyMembers"
                            ? "There are no family members to export."
                            : "There are no members to export.",
                    confirmBtn: "Ok",
                    isConfirm: (callback: any) => {
                        callback();
                    },
                });

                setConfirmPopup(true);
                return;
            }

            const formatDate = (date: any) =>
                date ? new Date(date).toLocaleDateString() : "";

            const dateSuffix = new Date().toISOString().slice(0, 10);
            let rows: Record<string, any>[] = [];
            let sheetName = "";

            if (exportType === "familyMembers") {
                rows = records.map((item: any) => ({
                    Id: item.id ?? "",
                    FirstName: item.firstName ?? "",
                    LastName: item.lastName ?? "",
                    EmailAddress: item.emailAddress ?? "",
                    UserName: item.userName ?? "",
                    GroupName: item.groupName ?? "",
                    PhoneNumber: item.phoneNumber ?? "",
                    Birthday: formatDate(item.birthday),
                    FilePath: item.filePath ?? "",
                    MemberName: item.memberName ?? "",
                    RelationToMember: item.relationToMember ?? "",
                    IsEnabled: item.isEnabled ?? "",
                    IsMinor: item.isMinor ?? "",
                    CreatedAt: formatDate(item.createdAt),
                    CreatedBy: item.createdBy ?? "",
                    UploadedAt: formatDate(item.uploadedAt),
                    UploadedBy: item.uploadedBy ?? "",
                    MemberRoleName: item.memberRoleName ?? "",
                }));
                sheetName = "Family Members";
            } else {
                rows = records.map((member: any) => ({
                    Id: member.id ?? "",
                    FamilyName: member.familyName ?? "",
                    FirstName: member.firstName ?? "",
                    LastName: member.lastName ?? "",
                    EmailAddress: member.emailAddress ?? "",
                    PhoneNumber: member.phoneNumber ?? "",
                    ImagePath: member.imagePath ?? "",
                    Street: member.street ?? "",
                    Street2: member.street2 ?? "",
                    City: member.city ?? "",
                    State: member.state ?? "",
                    Country: member.country ?? "",
                    Zip: member.zip ?? "",
                    Birthday: formatDate(member.birthday),
                    GroupName: member.groupName ?? "",
                    SubGroupName: member.subGroupName ?? "",
                    CreatedAt: formatDate(member.createdAt),
                    CreatedBy: member.createdBy ?? "",
                    UploadedAt: formatDate(member.uploadedAt),
                    UploadedBy: member.uploadedBy ?? "",
                }));
                sheetName = "Members";
            }

            const filePrefix =
                exportType === "familyMembers" ? "members" : "family";

            if (format === "excel") {
                const workbook = XLSX.utils.book_new();
                const worksheet = XLSX.utils.json_to_sheet(rows);
                XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
                XLSX.writeFile(workbook, `${filePrefix}-${dateSuffix}.xlsx`);
            } else {
                downloadCsv(rows, `${filePrefix}-${dateSuffix}.csv`);
            }
        } catch (error) {
            console.error(`${format.toUpperCase()} export failed:`, error);

            setConfirmData({
                title: "Warning",
                summary:
                    format === "excel"
                        ? exportType === "familyMembers"
                            ? "Failed to export family members to Excel."
                            : "Failed to export family to Excel."
                        : exportType === "familyMembers"
                            ? "Failed to export family members."
                            : "Failed to export family.",
                confirmBtn: "Ok",
                isConfirm: (callback: any) => {
                    callback();
                },
            });

            setConfirmPopup(true);
        } finally {
            setIsExporting(false);
        }
    };

    const exportButton = (
        <div
            ref={dropdownRef}
            className="relative"
        >
            <button
                type="button"
                onClick={() => setIsMenuOpen((previous) => !previous)}
                disabled={totalLength === 0}
                className="flex items-center justify-between gap-2 w-full xm:w-fit bg-secondary px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
                {buttonText}

                <svg
                    className={`h-4 w-4 transition-transform ${isMenuOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </button>

            {
                isMenuOpen && (
                    <div className="absolute right-0 z-50 mt-2 w-56 overflow-hidden border border-gray-200 bg-white shadow-lg">
                        <button
                            type="button"
                            disabled={totalLength === 0}
                            onClick={() => {
                                setItemsPerPage(defaultPerPage);

                                setShowExportPopup(true);
                                setIsMenuOpen(false);
                            }}
                            className="flex w-full items-center px-4 py-2.5 text-left text-sm hover:bg-gray-100"
                        >
                            Export Pdf
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                handleMemberExportAll("csv");
                                setIsMenuOpen(false);
                            }}
                            disabled={totalLength === 0 || isExporting}
                            className="flex w-full items-center px-4 py-2.5 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Export as CSV
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                handleMemberExportAll("excel");
                                setIsMenuOpen(false);
                            }}
                            disabled={totalLength === 0 || isExporting}
                            className="flex w-full items-center px-4 py-2.5 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Export as Excel
                        </button>
                    </div>
                )
            }
        </div>
    )


    const exportPopup =
        showExportPopup
            ? createPortal(
                <div
                    className="
                          fixed
                          inset-0
                          z-[999999]
                          bg-black/40
                          flex
                          items-center
                          justify-center
                          px-4
                          overflow-hidden
                      "
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                    }}
                >
                    <div
                        className="
                              bg-white
                              rounded-md
                              shadow-xl
                              w-full
                              max-w-[420px]
                              max-h-[90vh]
                              overflow-hidden
                              flex
                              flex-col
                          "
                    >
                        <div
                            className="
                                  flex
                                  items-center
                                  justify-between
                                  px-5
                                  py-4
                                  border-b
                                  shrink-0
                              "
                        >
                            <h2 className="text-lg font-semibold">
                                Export {title}
                            </h2>

                            <button
                                type="button"
                                disabled={
                                    isExporting
                                }
                                onClick={() =>
                                    setShowExportPopup(
                                        false
                                    )
                                }
                            >
                                <RxCross1
                                    size={18}
                                />
                            </button>
                        </div>

                        <div
                            className="
                                  px-5
                                  py-5
                                  overflow-y-auto
                              "
                        >
                            <label
                                className="
                                      block
                                      text-sm
                                      font-medium
                                      mb-2
                                  "
                            >
                                How many{" "}
                                {title.toLowerCase()}{" "}
                                would you like per
                                PDF page?
                            </label>

                            <input
                                type="number"
                                min={1}
                                max={maxPerPage}
                                value={
                                    itemsPerPage
                                }
                                disabled={
                                    isExporting
                                }
                                onChange={(e) =>
                                    setItemsPerPage(
                                        Number(
                                            e.target
                                                .value
                                        )
                                    )
                                }
                                className="
                                      w-full
                                      h-10
                                      border
                                      border-gray-300
                                      rounded-md
                                      px-3
                                      text-sm
                                      outline-none
                                      focus:border-secondary
                                  "
                            />

                            <p
                                className="
                                      text-xs
                                      text-gray-500
                                      mt-2
                                  "
                            >
                                Enter a value between
                                1 and{" "}
                                {maxPerPage}.
                            </p>

                            <div
                                className="
                                      mt-4
                                      bg-gray-50
                                      border
                                      rounded-md
                                      p-3
                                  "
                            >
                                <p className="text-sm text-gray-600">
                                    Total{" "}
                                    {title.toLowerCase()}
                                    :
                                    <span
                                        className="
                                              font-semibold
                                              text-black
                                              ml-1
                                          "
                                    >
                                        {
                                            totalLength
                                        }
                                    </span>
                                </p>

                                <p
                                    className="
                                          text-sm
                                          text-gray-600
                                          mt-1
                                      "
                                >
                                    Estimated pages:
                                    <span
                                        className="
                                              font-semibold
                                              text-black
                                              ml-1
                                          "
                                    >
                                        {itemsPerPage >
                                            0
                                            ? Math.ceil(
                                                totalLength /
                                                itemsPerPage
                                            )
                                            : 0}
                                    </span>
                                </p>
                            </div>
                        </div>

                        <div
                            className="
                                  flex
                                  justify-end
                                  gap-3
                                  px-5
                                  py-4
                                  border-t
                                  shrink-0
                              "
                        >
                            <button
                                type="button"
                                disabled={
                                    isExporting
                                }
                                onClick={() =>
                                    setShowExportPopup(
                                        false
                                    )
                                }
                                className="
                                      px-4
                                      py-2
                                      border
                                      border-gray-300
                                      rounded-md
                                      text-sm
                                      hover:bg-gray-50
                                  "
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                disabled={
                                    isExporting ||
                                    itemsPerPage <
                                    1 ||
                                    itemsPerPage >
                                    maxPerPage
                                }
                                onClick={
                                    handleExportPdf
                                }
                                className="
                                      px-4
                                      py-2
                                      bg-secondary
                                      hover:bg-custom-blue
                                      rounded-md
                                      text-white
                                      text-sm
                                      disabled:opacity-50
                                      flex
                                      items-center
                                      gap-2
                                  "
                            >
                                {isExporting && (
                                    <span
                                        className="
                                              w-4
                                              h-4
                                              border-2
                                              border-white
                                              border-r-transparent
                                              rounded-full
                                              animate-spin
                                          "
                                    />
                                )}

                                {isExporting
                                    ? "Creating PDF..."
                                    : "Export PDF"}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )
            : null;


    const pdfPreview =
        pdfUrl
            ? createPortal(
                <div
                    className="
                          fixed
                          inset-0
                          z-[1000000]
                          bg-black/50
                          flex
                          items-center
                          justify-center
                          p-4
                          sm:p-6
                          overflow-hidden
                      "
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                    }}
                >
                    <div
                        className="
                              bg-white
                              w-full
                              h-full
                              max-w-[1100px]
                              max-h-[92vh]
                              rounded-lg
                              shadow-2xl
                              flex
                              flex-col
                              overflow-hidden
                          "
                    >
                        <div
                            className="
                                  h-14
                                  shrink-0
                                  border-b
                                  flex
                                  items-center
                                  justify-between
                                  px-5
                              "
                        >
                            <h2
                                className="
                                      text-[16px]
                                      font-semibold
                                      text-gray-800
                                  "
                            >
                                {title} PDF
                            </h2>

                            <button
                                type="button"
                                onClick={() => {
                                    URL.revokeObjectURL(
                                        pdfUrl
                                    );

                                    setPdfUrl(null);
                                }}
                                className="
                                      w-8
                                      h-8
                                      flex
                                      items-center
                                      justify-center
                                      rounded-md
                                      text-gray-500
                                      hover:bg-gray-100
                                  "
                            >
                                <RxCross1
                                    size={18}
                                />
                            </button>
                        </div>

                        <div
                            className="
                                  flex-1
                                  min-h-0
                                  bg-gray-100
                                  p-4
                                  overflow-hidden
                              "
                        >
                            <iframe
                                src={pdfUrl}
                                title={`${title} PDF`}
                                className="
                                      w-full
                                      h-full
                                      border
                                      border-gray-300
                                      rounded
                                      bg-white
                                  "
                            />
                        </div>
                    </div>
                </div>,
                document.body
            )
            : null;

    return (
        <>
            {exportButton}

            {exportPopup}

            {pdfPreview}
        </>
    );
};

export default PdfDirectoryExport;